import type { IPublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PrimeReactProvider } from 'primereact/api'

import * as React from 'react'
import { Route, Routes } from 'react-router'
import { logger } from './common/logger'
import { MainLayout } from './components/MainLayout'
import { NoMatchPage } from './pages/NoMatchPage'

import { paths } from './paths'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import 'primeicons/primeicons.css'
import 'primeflex/primeflex.css'

const HomePage = React.lazy(async () => import('./pages/HomePage'))
const KnowledgeDatabasePage = React.lazy(async () => import('./pages/KnowledgeDatabase'))
const KnowledgeDatabaseFolderPage = React.lazy(async () => import('./pages/KnowledgeDatabaseFolder'))
const KnowledgeDatabaseQuestionPage = React.lazy(async () => import('./pages/KnowledgeDatabaseQuestion'))
const KnowledgeDatabaseSearchResultsPage = React.lazy(async () => import('./pages/KnowledgeDatabaseSearchResults'))

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error: unknown) => {
        logger.logError(error instanceof Error ? error : new Error(String(error)))
      },
    },
  },
})

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return <React.Suspense fallback={<>...</>}>{children}</React.Suspense>
}

interface AppProps {
  pca: IPublicClientApplication
}

export default function App({ pca }: AppProps) {
  return (
    <MsalProvider instance={pca}>
      <QueryClientProvider client={queryClient}>
        <PrimeReactProvider>
          <Routes>
            <Route path={paths.home} element={<MainLayout />}>
              <Route index element={<SuspenseWrapper><HomePage /></SuspenseWrapper>} />
              <Route path={paths.KnowledgeDatabase} element={<SuspenseWrapper><KnowledgeDatabasePage /></SuspenseWrapper>} />
              <Route path={paths.KnowledgeDatabaseFolder} element={<SuspenseWrapper><KnowledgeDatabaseFolderPage /></SuspenseWrapper>} />
              <Route path={paths.KnowledgeDatabaseQuestion} element={<SuspenseWrapper><KnowledgeDatabaseQuestionPage /></SuspenseWrapper>} />
              <Route path={paths.KnowledgeDatabaseSearchResults} element={<SuspenseWrapper><KnowledgeDatabaseSearchResultsPage /></SuspenseWrapper>} />
              <Route path="*" element={<NoMatchPage />} />
            </Route>
          </Routes>
        </PrimeReactProvider>
      </QueryClientProvider>
    </MsalProvider>
  )
}
